import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { Badge, Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from '@bliks-io/ui';
import type { ProcessListDraft, ProcessListRow } from '@bliks-io/contracts';
import { formatDateTime } from '@/lib/formatters';

interface EditorProcessRowProps {
  slug: string;
  process: ProcessListRow;
  departmentName: string | undefined;
}

export function EditorProcessRow({ slug, process, departmentName }: EditorProcessRowProps) {
  const { t } = useTranslation();
  const ownerName = [process.owner?.firstName, process.owner?.lastName].filter(Boolean).join(' ');

  return (
    <Item asChild>
      <Link to="/$slug/processes/editor/$processId" params={{ slug, processId: process._id }}>
        <ItemContent className="min-w-0">
          <ItemTitle className="w-full">
            <span className="truncate">{process.name}</span>
          </ItemTitle>
          {process.description.trim() !== '' && (
            <ItemDescription>{process.description}</ItemDescription>
          )}
          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
            {departmentName && (
              <>
                <span>{departmentName}</span>
                <span aria-hidden>·</span>
              </>
            )}
            <span>{t('processes.stepsCount', { count: process.stats.totalSteps })}</span>
            {ownerName !== '' && (
              <>
                <span aria-hidden>·</span>
                <span>{ownerName}</span>
              </>
            )}
          </p>
          {process.draft && <DraftEditLine draft={process.draft} />}
        </ItemContent>
        <ItemActions>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </ItemActions>
      </Link>
    </Item>
  );
}

function DraftEditLine({ draft }: { draft: ProcessListDraft }) {
  const { t, i18n } = useTranslation();
  const editedAt = formatDateTime(draft.lastEditedAt, i18n.language);
  const editorName = [draft.lastEditedBy?.firstName, draft.lastEditedBy?.lastName]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
      <Badge colorScheme="warning" size="sm">
        {t('processDraft.draftBadge')}
      </Badge>
      <span>
        {editorName === ''
          ? t('processes.draftEdited', { when: editedAt })
          : t('processes.draftEditedBy', { when: editedAt, name: editorName })}
      </span>
    </div>
  );
}
